import { Children, useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import PageInfoStripe from '../components/PageInfoStripe';
import ErrorPage from './ErrorPage';
import { useCollectionData } from '../hooks/useCollectionData';
import { languages, defaultLanguage, collectionPath } from '../config/languages';
import { normalizeUrl, archivePath } from '../utils/url';
import { useDocumentTitle } from '../hooks/useDocumentMeta';
import { useLanguage } from '../hooks/useLanguage';

const ITEMS_PER_PAGE = 15;

// ─── Collections intro blurb (collapsed to ~5 lines with "View more") ────────

// Localised level-0 intro labels. English is the canonical copy; cy is the
// reviewed Welsh translation (previously stranded in content/cy/themes/_index.md).
// Gaelic has no translation for this intro yet, so it falls back to English.
const INTRO_LABELS = {
  en: {
    heading: 'Collections and Themes', viewMore: 'View more', viewLess: 'View less',
    gridCollections: 'Collections', gridSubsections: 'Subsections', gridWebsites: 'Archived websites',
    // Said before each card list: how many cards are on this page, and which
    // of how many (screen readers; see CardList).
    onPage: {
      collections: (n, range) => `${n} ${n === 1 ? 'collection' : 'collections'} on this page${range ? ` (${range.from}–${range.to} of ${range.total})` : ''}`,
      subsections: (n, range) => `${n} ${n === 1 ? 'subsection' : 'subsections'} on this page${range ? ` (${range.from}–${range.to} of ${range.total})` : ''}`,
      websites: (n, range) => `${n} ${n === 1 ? 'website' : 'websites'} on this page${range ? ` (${range.from}–${range.to} of ${range.total})` : ''}`,
    },
  },
  cy: {
    heading: 'Casgliadau a Themâu', viewMore: 'Gweld mwy', viewLess: 'Gweld llai',
    gridCollections: 'Casgliadau', gridSubsections: 'Is-adrannau', gridWebsites: 'Gwefannau wedi’u harchifo',
    // The number after a colon avoids Welsh number-dependent mutations.
    onPage: {
      collections: (n, range) => `Casgliadau ar y dudalen hon: ${n}${range ? ` (${range.from}–${range.to} o ${range.total})` : ''}`,
      subsections: (n, range) => `Is-adrannau ar y dudalen hon: ${n}${range ? ` (${range.from}–${range.to} o ${range.total})` : ''}`,
      websites: (n, range) => `Gwefannau ar y dudalen hon: ${n}${range ? ` (${range.from}–${range.to} o ${range.total})` : ''}`,
    },
  },
};

function introLabels(langCode) {
  return INTRO_LABELS[langCode] ?? INTRO_LABELS.en;
}

function CollectionsIntro({ langCode }) {
  const [expanded, setExpanded] = useState(false);
  const labels = introLabels(langCode);
  const introId = useId();

  return (
    <div className="mt-4 mb-2">
      {/* Disclosure: the first two paragraphs are always shown; the rest is
          `hidden` until "View more". A CSS line-clamp left the clipped links in
          the Tab order, so keyboard focus could land on a link nobody could see
          (WCAG 2.4.11 Focus Not Obscured) and screen readers read text sighted
          users could not. */}
      {/* Only English and Welsh versions exist: on other locales (/gd) the
          English text is marked as English (N20, WCAG 3.1.2). */}
      <div
        id={introId}
        lang={langCode === 'en' || langCode === 'cy' ? undefined : 'en'}
        className="text-sm text-gray-600 dark:text-dark-400 leading-relaxed"
      >
        {langCode === 'cy' ? (
          <>
            <p className="mb-2">{'Cafodd y gwefannau archifiedig a gynhwysir yn y casgliadau hyn eu cipio rhwng 2005 a 2022, gan gynnwys y blynyddoedd hynny. Ar hyn o bryd, mae\'r casgliadau\'n statig, ond bydd gwefannau a archifwyd yn ddiweddar a chasgliadau newydd yn cael eu hychwanegu dros amser.'}</p>
            <p className="mb-2">{'Mae Archif We y DU yn curadu llawer o wahanol fathau o gasgliadau arbennig, gan gwmpasu ystod eang o bynciau a themâu sy\'n adlewyrchu bywyd cyfoes yn y DU.'}</p>
            <div hidden={!expanded}>
            <p className="mb-2">
              {'Caiff casgliadau eu creu gan ystod eang o gyfranwyr, gan gynnwys arbenigwyr pwnc a churaduron ar draws llyfrgelloedd adnau cyfreithiol y DU, sefydliadau treftadaeth ddiwylliannol, sefydliadau ymchwil, grwpiau cymunedol, ac unigolion. Hefyd, gall y cyhoedd enwebu gwefannau i\'w cynnwys trwy anfon e-bost at '}
              <a href="mailto:web-archivist@bl.uk" className="font-semibold text-accent-text underline underline-offset-2 hover:decoration-2 focus-visible:decoration-2 hover:text-blue-900 dark:hover:text-blue-300">
                web-archivist@bl.uk
              </a>
              {'.'}
            </p>
            <p className="mb-2">{'Ni all unrhyw gasgliad byth fod yn gwbl gynhwysfawr. Mae dewis gwefannau i\'w cynnwys yn broses a wneir â llaw, ac ni ellir archifo rhai gwefannau oherwydd cyfyngiadau technegol neu oherwydd eu bod y tu allan i gwmpas a meini prawf dethol y casgliad.'}</p>
            <p>{'I gael rhagor o wybodaeth am gasgliadau unigol, gan gynnwys eu cwmpas a\'u blaenoriaethau casglu, gweler y dogfennau sy\'n pennu cwmpas casgliadau sydd ar gael trwy gyfrwng ystorfa ymchwil y Llyfrgell Brydeinig.'}</p>
            </div>
          </>
        ) : (
          <>
            <p className="mb-2">
              The archived websites included in these collections were captured between 2005 and 2022
              inclusive. At present, the collections are static, although more recently archived
              websites and new collections will be added over time.
            </p>
            <p className="mb-2">
              The UK Web Archive curates many different types of special collections, covering a wide
              range of topics and themes reflecting contemporary life in the UK.
            </p>
            <div hidden={!expanded}>
            <p className="mb-2">
              Collections are created by a wide range of contributors, including subject specialists
              and curators across the UK legal deposit libraries, cultural heritage organisations,
              research institutions, community groups, and individuals. Members of the public can also
              nominate websites for inclusion by emailing{' '}
              <a href="mailto:web-archivist@bl.uk" className="font-semibold text-accent-text underline underline-offset-2 hover:decoration-2 focus-visible:decoration-2 hover:text-blue-900 dark:hover:text-blue-300">
                web-archivist@bl.uk
              </a>
              .
            </p>
            <p className="mb-2">
              No collection can ever be fully comprehensive. Selecting websites for inclusion is a
              manual process, and some websites cannot be archived because of technical limitations or
              because they fall outside the collection&apos;s scope and selection criteria.
            </p>
            <p>
              For more information about individual collections, including their scope and collecting
              priorities, see the collection scoping documents available through the{' '}
              {/* Opens in a new tab: say so to everyone, not only with the
                  arrow (WCAG 3.2.2; same cue as the cookie banner). */}
              <a
                href="https://bl.iro.bl.uk/entities/digitalcollection/91ca3d4a-0d72-46b6-b7fe-7ea45ccbaa45"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-accent-text underline underline-offset-2 hover:decoration-2 focus-visible:decoration-2 hover:text-blue-900 dark:hover:text-blue-300"
              >
                British Library research repository
                <span aria-hidden="true" className="ml-0.5">↗</span>
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
              .
            </p>
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        aria-controls={introId}
        className="mt-1 text-xs font-medium text-accent-text hover:underline rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-300 dark:focus-visible:ring-offset-dark-950"
      >
        {expanded ? labels.viewLess : labels.viewMore}
      </button>
    </div>
  );
}

// ─── Badge colour maps ────────────────────────────────────────────────────────

const DEPTH_COLOURS = {
  DEEP: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  CAPPED_LARGE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  CAPPED: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
};

const FREQ_COLOURS = {
  DAILY: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  WEEKLY: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  MONTHLY: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  QUARTERLY: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  SIXMONTHLY: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  ANNUAL: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const LICENCE_COLOURS = {
  GRANTED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  NOT_INITIATED: 'bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-dark-400',
  QUEUED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  NONE: 'bg-gray-100 text-gray-700 dark:bg-dark-700 dark:text-dark-300',
};

const SCOPE_COLOURS = {
  root: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  subdomains: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
};

// Licence statuses not surfaced as a badge on website cards.
const HIDDEN_LICENCE_STATUSES = new Set(['NOT_INITIATED', 'PENDING', 'GRANTED', 'QUEUED']);

// ─── Badge tooltips ───────────────────────────────────────────────────────────
// Plain-language explanations shown on hover for each card badge, so visitors
// can understand how often a website was captured and how much was collected.

// How often was the website archived?
const FREQ_TOOLTIPS = {
  DAILY: 'Daily – a snapshot was taken every day.',
  WEEKLY: 'Weekly – a snapshot was taken once a week.',
  MONTHLY: 'Monthly – a snapshot was taken once a month.',
  QUARTERLY: 'Quarterly – a snapshot was taken every three months.',
  SIXMONTHLY: '6-Monthly – a snapshot was taken every six months.',
  ANNUAL: 'Annual – a snapshot was taken once a year on a scheduled date.',
  DOMAINCRAWL: 'DOMAINCRAWL – the website was captured as part of our annual crawl of UK websites.',
};

// How much of the website was archived?
const DEPTH_TOOLTIPS = {
  CAPPED: 'CAPPED – up to 500 MB of content was collected.',
  CAPPED_LARGE: 'CAPPED LARGE – up to 2 GB of content was collected.',
  DEEP: 'DEEP – no size limit was applied, allowing a more extensive capture of the website.',
};

const SCOPE_TOOLTIPS = {
  subdomains: 'SUBDOMAINS – when archiving this website, we also captured content from related subdomains (for example, blog.example.org as well as www.example.org).',
};

const SINCE_TOOLTIP =
  'This shows when a targeted archive record was created for the website. It does not necessarily mean archiving began in that year; the website may have been archived earlier through other collecting activities.';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// A hover/focus tooltip rendered into <body> via a portal, so it is never
// clipped by a card's `overflow-hidden`. Native `title` tooltips proved
// unreliable across Chrome/Safari, so we position our own.
function Tooltip({ text, label, children }) {
  // The tooltip texts are English only: mark them on /cy and /gd (SR-6, 3.1.2).
  const langCode = useLanguage().code;
  const textLang = langCode === 'en' ? undefined : 'en';
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const id = useId();

  const hideTimer = useRef(null);

  const show = () => {
    clearTimeout(hideTimer.current);
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Anchor above the badge, centred and clamped to the viewport width.
    const left = Math.min(Math.max(r.left + r.width / 2, 12), window.innerWidth - 12);
    setCoords({ top: r.top, left });
  };
  const hide = () => {
    clearTimeout(hideTimer.current);
    setCoords(null);
  };
  // WCAG 1.4.13 Content on Hover or Focus — the tooltip must be hoverable, so
  // leaving the badge only hides it after a short grace period, during which
  // the pointer can move onto the tooltip itself and keep it open.
  const hideSoon = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setCoords(null), 300);
  };
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  // …and dismissible: Esc hides it without moving focus or the pointer.
  const visible = coords != null;
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        clearTimeout(hideTimer.current);
        setCoords(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible]);

  // The badge is a note (a label, not a control): a Tab stop so keyboard and
  // screen-reader users get each website's notes in full, and not a button, as
  // it does nothing when pressed. Its accessible name is the full note, e.g.
  // "Annual – a snapshot was taken once a year on a scheduled date." (bug #56,
  // 4.1.2). The note is the badge's own content — the visible label plus the
  // rest of the note, visually hidden — and the badge is named by itself, so it
  // is there the moment the badge receives focus (bug #16); the visible tooltip
  // is only mounted after focus/hover. NVDA and Narrator read a focused note's
  // content, not its name: with the content aria-hidden and the note in a
  // separate copy, every badge was announced "blank" (RRO bug #45). The name
  // keeps the visible text (2.5.3): if the note doesn't start with it ("Since
  // 2019"), the label is put in front. On /cy the English note is marked
  // lang="en" (3.1.2). A tap (touch, TalkBack, VoiceOver iOS double-tap)
  // focuses it and shows the tooltip.
  const startsWithLabel = text.toLowerCase().startsWith(String(label).toLowerCase());
  const rest = startsWithLabel ? text.slice(String(label).length) : ` – ${text}`;
  return (
    <span
      ref={triggerRef}
      tabIndex={0}
      role="note"
      id={id}
      aria-labelledby={id}
      className="inline-flex cursor-help rounded-full"
      onMouseEnter={show}
      onMouseLeave={hideSoon}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      <span className="sr-only" lang={textLang}>{rest}</span>
      {coords &&
        createPortal(
          <span
            aria-hidden="true"
            role="tooltip"
            lang={textLang}
            onMouseEnter={() => clearTimeout(hideTimer.current)}
            onMouseLeave={hideSoon}
            style={{
              position: 'fixed',
              top: coords.top - 8,
              left: coords.left,
              transform: 'translate(-50%, -100%)',
              maxWidth: 'min(20rem, calc(100vw - 1.5rem))',
            }}
            className="z-[100] rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-snug text-white shadow-xl dark:bg-dark-700"
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
}

function Badge({ label, colourClass, tooltip }) {
  const badge = (
    // min-h-[24px]: tooltip badges are focusable pointer targets, so they must
    // meet the 24×24 CSS px minimum (WCAG 2.5.8 Target Size).
    <span className={`inline-flex items-center min-h-[24px] min-w-[24px] px-2.5 py-1 rounded-full text-xs font-medium ${colourClass}`}>
      {label}
    </span>
  );
  return tooltip ? <Tooltip text={tooltip} label={label}>{badge}</Tooltip> : badge;
}

// A page of cards as a list, introduced by how many cards it holds: screen
// readers say "15 websites on this page (1–15 of 550)" before the cards, the
// list's name, and then "list, 15 items" on entering it and each card's
// position ("1 of 15"), so users know how many cards there are and where they
// are among them (WCAG 1.3.1). role="list" because Safari drops the list role
// of a list with list-style: none. Pagination moves focus to the count (SR-3);
// it is not a heading, so heading navigation goes h1 → card h2s (bug #65).
function CardList({ kind, page, total, langCode, className, children }) {
  const id = useId();
  const items = Children.toArray(children);
  const from = (page - 1) * ITEMS_PER_PAGE + 1;
  const range = total > items.length ? { from, to: from + items.length - 1, total } : null;
  return (
    <>
      <p id={id} className="sr-only" tabIndex={-1} data-results-heading>
        {introLabels(langCode).onPage[kind](items.length, range)}
      </p>
      <ul role="list" aria-labelledby={id} className={className}>
        {items.map(card => <li key={card.key} className="grid">{card}</li>)}
      </ul>
    </>
  );
}

function getDomain(url) {
  const parsed = normalizeUrl(url);
  return parsed ? parsed.hostname.replace('www.', '') : null;
}

// Localised badge labels shown on website cards. English is canonical; cy is
// the reviewed Welsh translation (Welsh translations August 2026). Values not
// covered by the translation (e.g. ANNUAL) fall back to English, and unknown
// codes (e.g. DOMAINCRAWL) are shown verbatim.
const FREQ_LABELS = {
  en: { DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', SIXMONTHLY: '6-Monthly', ANNUAL: 'Annual' },
  cy: { DAILY: 'Dyddiol', WEEKLY: 'Wythnosol', MONTHLY: 'Misol', QUARTERLY: 'Chwarterol', SIXMONTHLY: 'Chwemisol' },
};

const SCOPE_LABELS = {
  en: { subdomains: 'subdomains' },
  cy: { subdomains: 'Is-barthau' },
};

const SINCE_LABELS = { en: 'Since', cy: 'Ers' };

function formatFreq(f, langCode) {
  return FREQ_LABELS[langCode]?.[f] ?? FREQ_LABELS.en[f] ?? f;
}

function formatScope(scope, langCode) {
  return SCOPE_LABELS[langCode]?.[scope] ?? SCOPE_LABELS.en[scope] ?? scope;
}

function formatSince(year, langCode) {
  return `${SINCE_LABELS[langCode] ?? SINCE_LABELS.en} ${year}`;
}

function formatYear(dateStr) {
  return dateStr ? dateStr.slice(0, 4) : null;
}

function truncate(str, n) {
  if (!str || str.length <= n) return str;
  return str.slice(0, n).trimEnd() + '…';
}

function licenceBadgesFromSummary(summary) {
  if (!summary) return [];
  return Object.entries(summary)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({
      key: status,
      label: `${status.replace(/_/g, ' ')} (${count.toLocaleString()})`,
      colourClass: LICENCE_COLOURS[status] ?? 'bg-gray-100 text-gray-600',
    }));
}

// ─── Loading ──────────────────────────────────────────────────────────────────

// role="status": the loading text is announced, not only drawn (N18 / SR-2,
// WCAG 4.1.3). The messages are English only, so on /cy and /gd they are
// marked lang="en" until translations are added (3.1.2).
function Spinner({ message = 'Loading…' }) {
  const { code } = useLanguage();
  return (
    <div role="status" className="flex flex-col items-center gap-3 py-20">
      <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-primary" />
      <p lang={code === 'en' ? undefined : 'en'} className="text-gray-500 dark:text-dark-400 text-sm">{message}</p>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function buildPageWindows(page, totalPages) {
  const show = new Set([1, totalPages, page, page - 1, page - 2, page + 1, page + 2]);
  const sorted = [...show].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
    result.push(sorted[i]);
  }
  return result;
}

function Pagination({ page, totalPages, onPage }) {
  const { ui } = useLanguage();
  if (totalPages <= 1) return null;
  const navBtn = 'p-2 rounded-lg border border-transparent text-gray-500 dark:text-dark-400 hover:border-gray-600 dark:hover:border-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-300 dark:focus-visible:ring-offset-dark-950';
  // Hover and keyboard focus both give a visible, ≥ 3:1 change (border +
  // underline / focus ring), not just a near-invisible background tint.
  const pageBtn = (active) =>
    `w-9 h-9 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2 dark:focus-visible:ring-blue-300 dark:focus-visible:ring-offset-dark-950 ${
      active ? 'bg-blue-700 border-blue-700 text-white shadow-sm'
             : 'border-transparent text-gray-600 dark:text-dark-300 hover:border-gray-600 hover:text-gray-900 hover:underline dark:hover:border-dark-300 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-700'
    }`;
  const windows = buildPageWindows(page, totalPages);

  return (
    <nav aria-label={ui.pagination} className="flex flex-wrap items-center justify-center gap-1 mt-8">
      <button type="button" data-nav-id="page-prev" onClick={() => onPage(page - 1)} disabled={page === 1} className={navBtn} aria-label={ui.previousPage}>
        <svg aria-hidden="true" focusable="false" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      {windows.map((w, i) =>
        w === '…' ? (
          <span key={`d-${i}`} aria-hidden="true" className="w-9 h-9 flex items-center justify-center text-gray-600 dark:text-dark-400 text-sm select-none">…</span>
        ) : (
          <button
            key={w}
            type="button"
            data-nav-id={`page-${w}`}
            onClick={() => onPage(w)}
            className={pageBtn(w === page)}
            aria-label={ui.pageN(w)}
            aria-current={w === page ? 'page' : undefined}
          >
            {w}
          </button>
        )
      )}
      <button type="button" data-nav-id="page-next" onClick={() => onPage(page + 1)} disabled={page === totalPages} className={navBtn} aria-label={ui.nextPage}>
        <svg aria-hidden="true" focusable="false" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {/* Announced when the page changes (SR-3, WCAG 4.1.3). */}
      <span role="status" className="ml-2 text-sm text-gray-600 dark:text-dark-400">{ui.pageOf(page, totalPages)}</span>
    </nav>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────────

// ─── Cover image helpers ──────────────────────────────────────────────────────

const FALLBACK_COVER = '/images/TopicsThemes/collections/collection_default.webp';

// Cover sized by the padding-bottom aspect-ratio hack rather than CSS
// `aspect-ratio`. Safari collapses an `aspect-ratio` flex child to 0 height
// when every descendant is `position: absolute`, leaving the cover unpainted;
// padding-bottom drives layout off the parent's resolved width and works
// identically in Safari/Chrome/Edge. Children use `absolute inset-0` to fill.
function CoverFrame({ paddingBottom, children }) {
  return (
    <div className="relative w-full shrink-0 overflow-hidden bg-gray-100 dark:bg-dark-900" style={{ paddingBottom }}>
      {children}
    </div>
  );
}

// Covers render as <img>, not a CSS background, so they can lazy-load: only the
// first row is fetched up front and the rest arrive on scroll. `priority` marks
// that first row — it must NOT be lazy, since the leading cover is the likely
// LCP element. Decorative: the card's accessible name comes from its heading, so
// a real alt here would double-announce the collection name inside the link.
// Sized 800x450 to match the encoder in scripts/optimise-collection-images.mjs.
function CoverImage({ src, priority = false, className = '' }) {
  return (
    <img
      src={src ?? FALLBACK_COVER}
      alt=""
      aria-hidden="true"
      width={800}
      height={450}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={e => {
        if (!e.currentTarget.src.endsWith(FALLBACK_COVER)) e.currentTarget.src = FALLBACK_COVER;
      }}
      className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${className}`}
    />
  );
}

function StatPill({ value, label, accent = 'default' }) {
  const accents = {
    default: 'text-gray-900 dark:text-dark-100',
    success: 'text-emerald-600 dark:text-emerald-400',
    info: 'text-blue-600 dark:text-blue-400',
    target: 'text-blue-600 dark:text-white',
  };
  return (
    <div>
      <div className={`text-lg font-bold tracking-tight ${accents[accent]}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.08em] text-gray-600 dark:text-dark-400 font-semibold mt-0.5">
        {label}
      </div>
    </div>
  );
}

function LicenceBar({ licence, total }) {
  if (!licence || !total) return null;
  const order = ['GRANTED', 'PENDING', 'QUEUED', 'NOT_INITIATED', 'NONE'];
  const colours = {
    GRANTED: 'bg-emerald-500',
    PENDING: 'bg-amber-400',
    QUEUED: 'bg-blue-400',
    NOT_INITIATED: 'bg-gray-300 dark:bg-dark-600',
    NONE: 'bg-gray-200 dark:bg-dark-700',
  };
  const segs = order
    .map(k => ({ k, n: licence[k] ?? 0 }))
    .filter(s => s.n > 0);
  if (segs.length === 0) return null;
  return (
    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-gray-100 dark:bg-dark-700">
      {segs.map(s => (
        <div
          key={s.k}
          className={colours[s.k]}
          style={{ width: `${(s.n / total) * 100}%` }}
          title={`${s.k.replace(/_/g, ' ')}: ${s.n.toLocaleString()}`}
        />
      ))}
    </div>
  );
}

// ─── Dataset card (top-level theme) ───────────────────────────────────────────

// Cards are real links (<Link>), not buttons: activating one navigates to
// another view, so it must be announced as a link and support open-in-new-tab
// (WCAG 4.1.2 Name, Role, Value).
function DatasetCard({ dataset, to, state, priority = false }) {
  const { ui } = useLanguage();
  const root = dataset.collections[dataset.collectionId];
  const yearMin = formatYear(root?.crawlStartMin);
  const yearMax = formatYear(root?.crawlStartMax);
  const subCount = root?.children?.length ?? 0;
  const cover = root?.coverImage ?? FALLBACK_COVER;
  const coverage = yearMin && yearMax ? (yearMin === yearMax ? yearMin : `${yearMin}–${yearMax}`) : '—';
  const titleId = useId();
  const descId = useId();

  // Named by the title only; the chip, id and counts are its description.
  // Before, the whole card text was the name: "COLLECTION #910 Brexit 13
  // subsections View" (SR-5, WCAG 2.4.4).
  return (
    <Link
      to={to}
      state={state}
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="group relative text-left w-full flex flex-col bg-white dark:bg-dark-800 rounded-2xl border border-gray-500 dark:border-dark-400 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2"
    >
      {/* Cover — 16:9 via padding-bottom hack */}
      <CoverFrame paddingBottom="56.25%">
        <CoverImage src={cover} priority={priority} />

        {/* Legibility gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />

        {/* Top chips */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-md rounded-full px-3 py-1 text-[11px] text-white font-semibold border border-white/20 uppercase tracking-wider">
            {ui.collection}
          </div>
          <span className="inline-flex items-center bg-black/70 backdrop-blur-md rounded-md px-2 py-1 text-[11px] text-white font-mono tabular-nums">
            #{dataset.collectionId}
          </span>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          {/* Screen readers announce top-level collections as level-1 headings,
              like the collection's own page title (BL decision, 09.10.2026). The
              element and its styles are unchanged (h3), so the card looks the same;
              only the level exposed to assistive technology is 1. The page title
              "Collections and Themes" stays the first h1. */}
          <h3 id={titleId} aria-level={1} className="text-white text-xl md:text-2xl font-bold leading-tight tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            {dataset.collectionName}
          </h3>
          <span id={descId} className="sr-only">
            {ui.collection} #{dataset.collectionId}{subCount > 0 ? `, ${ui.subsections(subCount)}` : ''}
          </span>
          {subCount > 0 && (
            <p className="text-white text-xs mt-1.5 font-medium">
              {ui.subsections(subCount)}
            </p>
          )}
        </div>
      </CoverFrame>

      {/* Body */}
      <div className="flex flex-col flex-1 w-full p-5 gap-4">
        <div className="mt-auto w-full flex items-center pt-3 border-t border-gray-500 dark:border-dark-400">
          <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-accent-text group-hover:gap-2 transition-all">
            View
            <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── Sub-collection card ──────────────────────────────────────────────────────

function FolderGlyph({ className }) {
  return (
    <svg aria-hidden="true" focusable="false" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
    </svg>
  );
}

function CollectionCard({ collection, to, state }) {
  const { ui } = useLanguage();
  const childCount = collection.children?.length ?? 0;
  const hasChildren = childCount > 0;
  const itemCount = collection.subtreeItemCount ?? collection.directItemCount ?? 0;
  const titleId = useId();
  const descId = useId();

  // Named by the title only; id and counts are its description (SR-5).
  return (
    <Link
      to={to}
      state={state}
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="group relative text-left w-full flex flex-col gap-2.5 bg-white dark:bg-dark-800 rounded-xl border border-gray-500 dark:border-dark-400 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-blue-700 dark:hover:border-blue-300 transition-all duration-200 overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 p-3.5"
    >
      {/* Title row: folder glyph + name + id */}
      <div className="flex items-start gap-2.5">
        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-accent-primary/10 dark:bg-accent-primary/15 text-accent-text shrink-0">
          <FolderGlyph className="w-4 h-4" />
        </span>
        <h2 id={titleId} className="flex-1 min-w-0 text-sm font-bold leading-snug text-gray-900 dark:text-dark-100 line-clamp-2">
          {collection.name}
        </h2>
        <span id={descId} className="sr-only">
          #{collection.id}
          {hasChildren ? `, ${childCount} ${ui.subsectionWord(childCount)}` : ''}
          {itemCount > 0 ? `, ${itemCount.toLocaleString()} ${ui.websiteWord(itemCount)}` : ''}
        </span>
        <span className="shrink-0 text-[10px] font-mono tabular-nums text-gray-600 dark:text-dark-400 mt-0.5">
          #{collection.id}
        </span>
      </div>

      {/* Stats */}
      <div className="mt-auto flex items-center gap-4">
        {hasChildren && (
          <StatPill value={childCount} label={ui.subsectionWord(childCount)} accent="info" />
        )}
        {itemCount > 0 && (
          <StatPill value={itemCount.toLocaleString()} label={ui.websiteWord(itemCount)} accent="target" />
        )}
      </div>

      {/* View affordance */}
      <div className="flex items-center pt-2 border-t border-gray-500 dark:border-dark-400">
        <span className="ml-auto inline-flex items-center gap-1 text-[13px] font-semibold text-accent-text group-hover:gap-2 transition-all">
          {hasChildren ? ui.cardBrowse : ui.cardViewWebsites}
          <svg aria-hidden="true" focusable="false" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

// Every website card links to the archive's capture calendar (wayback), not the
// live site. Say so in the link text so the destination isn't a surprise
// (WCAG 2.4.4 Link Purpose).
const ARCHIVE_LINK_LABELS = {
  en: { cue: 'Archived versions', sr: (d) => `view archived versions${d ? ` of ${d}` : ''}` },
  cy: { cue: 'Fersiynau wedi’u harchifo', sr: (d) => `gweld fersiynau wedi’u harchifo${d ? ` o ${d}` : ''}` },
};

function ItemCard({ item, langCode }) {
  const archiveLabels = ARCHIVE_LINK_LABELS[langCode] ?? ARCHIVE_LINK_LABELS.en;
  const parsedSeed = normalizeUrl(item['Primary Seed']);
  const domain = parsedSeed ? parsedSeed.hostname.replace('www.', '') : null;
  const faviconUrl = domain
    ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`
    : null;
  const year = formatYear(item['Crawl Start Date']);
  // Every target/URL points at the archive's wayback-style capture list.
  const archiveHref = parsedSeed ? archivePath(parsedSeed) : null;

  return (
    <div className="group flex flex-col bg-white dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
      <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-blue-400 to-accent-secondary" />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start gap-3">
          {faviconUrl && (
            <img
              src={faviconUrl} alt="" width={24} height={24}
              className="mt-0.5 rounded flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity"
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <h2 className="text-sm font-semibold text-gray-900 dark:text-dark-100 leading-snug">
            {archiveHref ? (
              <a href={archiveHref} className="hover:text-accent-text hover:underline">
                {item['Title of Target']}
                <span className="sr-only"> – {archiveLabels.sr(domain)}</span>
              </a>
            ) : (
              item['Title of Target']
            )}
          </h2>
        </div>
        <p className="text-xs text-gray-500 dark:text-dark-400 leading-relaxed flex-1">
          {truncate(item['Description'], 130)}
        </p>
        {archiveHref ? (
          // Same destination as the title link, so it is kept out of the Tab
          // order and hidden from screen readers (which get the domain in the
          // title link's text) to avoid a duplicate link.
          <a
            href={archiveHref}
            tabIndex={-1}
            aria-hidden="true"
            className="flex items-center gap-1.5 text-xs text-accent-text hover:underline min-w-0"
            title={item['Primary Seed']}
          >
            <svg aria-hidden="true" focusable="false" className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium shrink-0">{archiveLabels.cue}:</span>
            <span className="font-mono truncate">{domain}</span>
          </a>
        ) : (
          <span
            className="block text-xs text-gray-600 dark:text-dark-400 font-mono truncate"
            title={item['Primary Seed']}
          >
            {item['Primary Seed']}
          </span>
        )}
        <div className="flex flex-wrap gap-2 mt-auto pt-2 border-t border-gray-100 dark:border-dark-700">
          {item['Depth'] && <Badge label={item['Depth'].replace(/_/g, ' ')} colourClass={DEPTH_COLOURS[item['Depth']] ?? 'bg-gray-100 text-gray-600'} tooltip={DEPTH_TOOLTIPS[item['Depth']]} />}
          {item['Crawl Frequency'] && <Badge label={formatFreq(item['Crawl Frequency'], langCode)} colourClass={FREQ_COLOURS[item['Crawl Frequency']] ?? 'bg-gray-100 text-gray-600'} tooltip={FREQ_TOOLTIPS[item['Crawl Frequency']]} />}
          {item['Scope'] && item['Scope'] !== 'root' && <Badge label={formatScope(item['Scope'], langCode)} colourClass={SCOPE_COLOURS[item['Scope']] ?? 'bg-gray-100 text-gray-600'} tooltip={SCOPE_TOOLTIPS[item['Scope']]} />}
          {item['Licence Status'] && !HIDDEN_LICENCE_STATUSES.has(item['Licence Status']) && <Badge label={item['Licence Status'].replace(/_/g, ' ')} colourClass={LICENCE_COLOURS[item['Licence Status']] ?? 'bg-gray-100 text-gray-600'} />}
          {year && <Badge label={formatSince(year, langCode)} colourClass="bg-gray-100 text-gray-700 dark:bg-dark-700 dark:text-dark-300" tooltip={SINCE_TOOLTIP} />}
        </div>
      </div>
    </div>
  );
}

// Everything the search bar and its results area say, so a Welsh page never
// announces English (WCAG 3.1.2). The Welsh count avoids number-dependent
// mutations ("canlyniad / chanlyniad") by putting the number after a colon.
const SEARCH_LABELS = {
  en: {
    label: 'Search websites in this collection', placeholder: 'e.g. title, description or domain', clear: 'Clear search',
    results: (n, term) => <>{n} result{n !== 1 ? 's' : ''} for &ldquo;{term}&rdquo;</>,
    broader: '. Try a broader term.',
    noMatch: 'No websites match your search.',
    empty: 'No websites in this collection.',
    loading: 'Loading websites…',
  },
  cy: {
    label: 'Chwilio gwefannau yn y casgliad hwn', placeholder: 'e.e. teitl, disgrifiad neu barth', clear: 'Clirio’r chwiliad',
    results: (n, term) => <>Canlyniadau ar gyfer &ldquo;{term}&rdquo;: {n}</>,
    broader: '. Rhowch gynnig ar derm ehangach.',
    noMatch: 'Does dim gwefannau’n cyfateb i’ch chwiliad.',
    empty: 'Does dim gwefannau yn y casgliad hwn.',
    loading: 'Wrthi’n llwytho gwefannau…',
  },
};
const searchLabels = (langCode) => SEARCH_LABELS[langCode] ?? SEARCH_LABELS.en;

// A persistent, visible <label> names the field; the placeholder only gives
// examples and disappears as the user types, so it can't be the only label
// (WCAG 3.3.2 / 1.3.1, Report2 OA 44). Placeholder ≥ 7:1 (gray-600 / dark-300).
function SearchBar({ value, onChange, count, term, langCode }) {
  const labels = searchLabels(langCode);
  const inputId = useId();
  const inputRef = useRef(null);
  return (
    <div className="max-w-md">
      <label htmlFor={inputId} className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-dark-200">
        {labels.label}
      </label>
      <div className="relative">
        <svg aria-hidden="true" focusable="false" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-dark-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          id={inputId}
          ref={inputRef}
          type="text" placeholder={labels.placeholder} value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-800 dark:text-dark-100 placeholder-gray-600 dark:placeholder-dark-300 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
        />
        {value && (
          <button
            type="button"
            // The button disappears once the field is empty: put focus back in
            // the field rather than letting it fall to <body> (WCAG 2.4.3).
            // 28×28 px target (2.5.8, minimum 24×24); icon ≥ 3:1 (1.4.11).
            onClick={() => { onChange(''); inputRef.current?.focus(); }}
            aria-label={labels.clear}
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-600 hover:text-gray-900 dark:text-dark-300 dark:hover:text-dark-100">
            <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {/* Always rendered (only the text changes) so the result count is
          announced as a status message while typing (WCAG 4.1.3). */}
      <p
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={`text-sm text-gray-500 dark:text-dark-400 ${term ? 'mt-2' : 'sr-only'}`}
      >
        {term && (
          <>
            {labels.results(count, term)}
            {count === 0 && labels.broader}
          </>
        )}
      </p>
    </div>
  );
}

// "Back to …" is a link to the parent collection. When the user actually came
// from that parent, it steps back through history instead of pushing a new
// entry, so it behaves exactly like the browser / voice-control "Go back"
// command: scroll position and focus on the card they opened are restored (see
// ScrollToTop), and a following "Go back" leaves the collections area as
// expected rather than bouncing between the two pages. Pages turned inside this
// collection are history entries too (`pageSteps`), so it skips over them to
// reach the parent list rather than the previous page of this one.
// It is the first control in <main>, so "Skip to main content" followed by Tab
// reaches it: "Back to all themes" on a first-level collection (bug #55).
function BackButton({ label, to }) {
  const { ui } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromParent = location.state?.from === to;
  const pageSteps = location.state?.pageSteps ?? 0;
  return (
    <Link
      to={to}
      onClick={(e) => {
        if (cameFromParent && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
          e.preventDefault();
          navigate(-(1 + pageSteps));
        }
      }}
      className="inline-flex items-center gap-1.5 text-sm text-accent-text hover:text-blue-900 dark:hover:text-blue-300 font-medium mb-4 transition-colors">
      <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      {ui.backTo(label)}
    </Link>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CollectionsPage() {
  const {
    manifest, loadingManifest, error,
    loadCollectionItems, isItemsLoading,
    getCollection, getChildren, getAncestors,
  } = useCollectionData();

  const { lang, collectionId } = useParams();

  // The locale is taken straight from the URL and is the basis for every link
  // this page builds. An unknown locale (e.g. /xx/ukwa/collection/5) is sent to
  // the English equivalent so there is always one canonical, valid URL.
  const isKnownLang = Object.prototype.hasOwnProperty.call(languages, lang);
  const langCode = isKnownLang ? lang : defaultLanguage;

  // The page number lives in the URL (?page=3). Turning a page adds a history
  // entry, so browser "Go back" walks back through the pages the user saw
  // (5 → 3 → 1), and "Go back" from a collection returns to the page of the list
  // the user left (page 5, not page 1). A page can also be bookmarked or reloaded
  // (WCAG 2.4.3 / 3.2.3: predictable navigation). Resetting to page 1 while
  // typing in the filter replaces the entry instead, so typing adds no history.
  // Values out of range are clamped where each list is rendered.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
  const location = useLocation();
  // Keep the entry's router state when paging: it records the list page the
  // user came from, which lets "Back to …" step back to it (page 3, not page 1)
  // instead of opening the parent afresh (N30 / RRO 52). `pageSteps` counts the
  // page entries added since arriving here, so "Back to …" can skip them.
  const routerState = location.state;
  const setPage = useCallback((p, { push = false } = {}) => {
    if (p === page) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (p > 1) next.set('page', String(p));
      else next.delete('page');
      return next;
    }, {
      replace: !push,
      preventScrollReset: true,
      state: push ? { ...routerState, pageSteps: (routerState?.pageSteps ?? 0) + 1 } : routerState,
    });
  }, [setSearchParams, routerState, page]);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(null);

  // The visible tree position is derived entirely from the URL param. A single
  // collection id is enough because getAncestors() rebuilds the full
  // root → … → current chain. `manifest` is a dep because getCollection /
  // getAncestors read a ref that's only populated once the manifest loads, so a
  // direct deep-link recomputes the path as soon as the data arrives.
  const path = useMemo(() => {
    if (collectionId == null) return [];
    const id = Number(collectionId);
    if (Number.isNaN(id) || !getCollection(id)) return [];
    return [...getAncestors(id).map(a => a.id), id];
    // `manifest` is intentional: getCollection/getAncestors read a ref that is
    // only filled once the manifest loads, so a deep-link must re-derive then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, getCollection, getAncestors, manifest]);

  // Clear the leaf filter whenever the collection changes. (No page reset: a new
  // collection's URL has no ?page, and resetting here would throw away the page
  // restored by "Go back".)
  useEffect(() => {
    setSearch('');
  }, [collectionId]);

  const currentId = path[path.length - 1] ?? null;
  const current = currentId != null ? getCollection(currentId) : null;
  const children = currentId != null ? getChildren(currentId) : [];
  const isLeaf = current ? children.length === 0 : false;
  const itemsLoading = currentId != null && isItemsLoading(currentId);

  // Fetch items only when we land on a leaf
  useEffect(() => {
    if (currentId != null && isLeaf) {
      let cancelled = false;
      setItems(null);
      loadCollectionItems(currentId).then(data => { if (!cancelled) setItems(data ?? []); });
      return () => { cancelled = true; };
    }
    setItems(null);
  }, [currentId, isLeaf, loadCollectionItems]);

  // All in-tree navigation flows through the URL: /{lang}/ukwa/collection/{id}
  // (or the bare base for the top-level index). Drilling in, breadcrumb jumps
  // and back all just change the route; `path` re-derives from the new param,
  // and ScrollToTop resets scroll on the route change.

  const parentPath = collectionPath(langCode, path.length >= 2 ? path[path.length - 2] : null);
  // Passed as router state on card links so BackButton knows it can step back.
  const fromHere = { from: location.pathname };

  const handlePageChange = useCallback((p) => {
    setPage(p, { push: true });
    // Respect "Reduce motion" (panel setting or OS preference): jump, don't glide.
    const reduce =
      document.documentElement.classList.contains('reduce-motion') ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    // Start keyboard and screen-reader users at the new results, not on the
    // pagination at the bottom (SR-3, WCAG 2.4.3).
    requestAnimationFrame(() => {
      document.querySelector('#main-content [data-results-heading]')?.focus({ preventScroll: true });
    });
  }, [setPage]);

  // ── Breadcrumbs ──────────────────────────────────────────────────────────
  const langConfig = languages[langCode];
  const breadcrumbs = (() => {
    const base = [
      { label: langConfig.homeLabel, path: langConfig.urlPrefix || '/' },
      // Ancestors are links (real URLs), not buttons, so they are found with
      // link navigation and can be opened in a new tab (N19).
      { label: introLabels(langCode).gridCollections, path: path.length > 0 ? collectionPath(langCode, null) : undefined },
    ];
    path.forEach((id, i) => {
      const c = getCollection(id);
      const isLast = i === path.length - 1;
      base.push({
        label: c?.name ?? `#${id}`,
        path: isLast ? undefined : collectionPath(langCode, id),
      });
    });
    return base;
  })();

  // ── Page title (2.4.2) ───────────────────────────────────────────────────
  // Null while ErrorPage renders, so its own title is not overwritten.
  useDocumentTitle(
    error || !isKnownLang
      ? null
      : path.length === 0
        ? introLabels(langCode).heading
        : current?.name ?? introLabels(langCode).heading,
    langCode
  );

  // ── Invalid locale → canonical English URL ───────────────────────────────
  if (!isKnownLang) {
    return <Navigate to={collectionPath(defaultLanguage, collectionId)} replace />;
  }

  // ── Error / loading ──────────────────────────────────────────────────────
  // The collections data is a linked service; if it cannot be reached this is a
  // 503, not a broken page. Future external services should reuse ErrorPage 503.
  if (error) {
    return (
      <ErrorPage
        code={503}
        langCode={langCode}
        detail={error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (loadingManifest || !manifest) {
    return (
      <>
        <PageInfoStripe breadcrumbs={breadcrumbs} />
        <Spinner message="Loading collections…" />
      </>
    );
  }

  // ── Level 0: theme/dataset index ─────────────────────────────────────────
  if (path.length === 0) {
    const indexPages = Math.ceil(manifest.datasets.length / ITEMS_PER_PAGE);
    const indexPage = Math.min(page, Math.max(1, indexPages));
    return (
      <>
        <PageInfoStripe breadcrumbs={breadcrumbs} />
        <main className="max-w-7xl mx-auto px-6 py-10">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-xs font-semibold text-accent-text uppercase tracking-widest mb-2">
              <span className="w-8 h-px bg-accent-primary inline-block" />
              UK Web Archive
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-100 leading-tight">
              {introLabels(langCode).heading}
            </h1>
            <CollectionsIntro langCode={langCode} />
          </div>

          <CardList kind="collections" page={indexPage} total={manifest.datasets.length} langCode={langCode} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {manifest.datasets.slice((indexPage - 1) * ITEMS_PER_PAGE, indexPage * ITEMS_PER_PAGE).map((ds, i) => (
              <DatasetCard
                key={ds.collectionId}
                dataset={ds}
                priority={i < 3}
                to={collectionPath(langCode, ds.collectionId)}
                state={fromHere}
              />
            ))}
          </CardList>
          <Pagination
            page={indexPage}
            totalPages={indexPages}
            onPage={handlePageChange}
          />
        </main>
      </>
    );
  }

  // ── Levels 1+: a collection (leaf or branch) ─────────────────────────────
  if (!current) {
    return (
      <>
        <PageInfoStripe breadcrumbs={breadcrumbs} />
        <div className="text-center py-20 text-gray-600 dark:text-dark-400">Collection not found.</div>
      </>
    );
  }

  const ancestors = getAncestors(currentId);
  const parent = ancestors[ancestors.length - 1] ?? null;
  const firstLevel = path.length === 1;
  const backLabel = firstLevel ? 'all themes' : (parent?.name ?? 'previous');

  // Branch view: render child collection cards
  if (!isLeaf) {
    const totalPages = Math.ceil(children.length / ITEMS_PER_PAGE);
    const branchPage = Math.min(page, Math.max(1, totalPages));
    const paginated = children.slice((branchPage - 1) * ITEMS_PER_PAGE, branchPage * ITEMS_PER_PAGE);

    return (
      <>
        <PageInfoStripe breadcrumbs={breadcrumbs} />
        <main className="max-w-7xl mx-auto px-6 py-10">
          <BackButton label={backLabel} to={parentPath} />

          <div className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-900 dark:to-dark-800 rounded-2xl p-6 text-white mb-8">
            <div className="text-blue-50 text-sm font-medium mb-1">{langConfig.ui.collectionNo(current.id)}</div>
            <h1 className="text-2xl md:text-3xl font-bold leading-snug mb-2">{current.name}</h1>
            <p className="text-blue-50 text-sm">
              {langConfig.ui.subsections(children.length)}
            </p>
          </div>

          <CardList kind="subsections" page={branchPage} total={children.length} langCode={langCode} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginated.map(c => <CollectionCard key={c.id} collection={c} to={collectionPath(langCode, c.id)} state={fromHere} />)}
          </CardList>
          <Pagination page={branchPage} totalPages={totalPages} onPage={handlePageChange} />
        </main>
      </>
    );
  }

  // Leaf view: items within this collection
  const filtered = (items ?? []).filter(i => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      i['Title of Target']?.toLowerCase().includes(s) ||
      i['Description']?.toLowerCase().includes(s) ||
      (getDomain(i['Primary Seed']) ?? '').toLowerCase().includes(s)
    );
  });
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const leafPage = Math.min(page, Math.max(1, totalPages));
  const paginated = filtered.slice((leafPage - 1) * ITEMS_PER_PAGE, leafPage * ITEMS_PER_PAGE);

  return (
    <>
      <PageInfoStripe breadcrumbs={breadcrumbs} />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <BackButton label={backLabel} to={parentPath} />

        <div className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-900 dark:to-dark-800 rounded-2xl p-6 text-white mb-6">
          <div className="text-blue-50 text-sm font-medium mb-1">
            {langConfig.ui.collectionNo(current.id)}
            {parent && <> · {langConfig.ui.partOf(parent.name)}</>}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold leading-snug mb-2">{current.name}</h1>
        </div>

        <SearchBar value={search} onChange={s => { setSearch(s); setPage(1); }} count={filtered.length} term={search} langCode={langCode} />

        {itemsLoading || items === null ? (
          <Spinner message={searchLabels(langCode).loading} />
        ) : paginated.length > 0 ? (
          <>
          <CardList kind="websites" page={leafPage} total={filtered.length} langCode={langCode} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {paginated.map(item => <ItemCard key={item['Record ID']} item={item} langCode={langCode} />)}
          </CardList>
          </>
        ) : (
          <div className="text-center py-20 text-gray-600 dark:text-dark-400">
            <svg aria-hidden="true" focusable="false" className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {search.trim() ? searchLabels(langCode).noMatch : searchLabels(langCode).empty}
          </div>
        )}

        <Pagination page={leafPage} totalPages={totalPages} onPage={handlePageChange} />
      </main>
    </>
  );
}
